import { ArrowLeftOutlined } from "@ant-design/icons";
import { Space } from "antd";
import { message } from "utils/ciq-message";
import DefaultTag from "components/widgets/default-tag";
import WorkflowTemplateEditor from "components/workflow-template-editor";
import { TSaveWorkflowModelInput } from "components/workflow-template-editor/model";
import { useCIQMutation, useCIQQuerySubscription } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GET_DB_SUBSCRIPTION_TEMPLATES } from "services/graphQL/ciq-queries";
import {
  MUTATION_COPY_SUBSCRIPTION_TEMPLATE,
  MUTATION_SET_DEFAULT_SUBSCRIPTION_WF_TEMPLATE,
  MUTATION_UPDATE_SUBSCRIPTION_MDB_TEMPLATE,
  MUTATION_UPDATE_SUBSCRIPTION_TEMPLATE_API
} from "services/graphQL/mutations";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { FeatureIdTemplateMapping, FeatureTypes } from "entity-app/constants";
import { ErrorMessages, SuccessMessages } from "../../constants";
import FeatureTemplateList from "./feature-template-list";

const instructionsTextForFeatures = {
  [FeatureTypes.DESIGN_PACKAGE]:
    "Create the Design Package date block templates you wish to use across projects. It is important to note that new templates added or any modifications made to existing templates here, will not impact existing projects.",
  [FeatureTypes.BID_PACKAGE]:
    "Create the Bid Package date block templates you wish to use across projects. It is important to note that new templates added or any modifications made to existing templates here, will not impact existing projects.",
  [FeatureTypes.MATERIALS]:
    "Create the material date block templates you wish to use across projects. It is important to note that new templates added or any modifications made to existing templates here, will not impact existing projects."
};

function FeatureTemplateSubscriptionSettings(props: { featureId: number }) {
  const { featureId } = props;

  const [detailsViewData, setdetailsViewData] = useState<any>(null);
  const [savingDefaultTemplate, setSavingDefaultTemplate] = useState(false);
  const [featureTemplates, setfeatureTemplates] = useState<any>();

  const { subscriptionRole } = useContext(SubscriptionContext);

  const { data: DBTemplates } = useCIQQuerySubscription<{
    subscription_workflow_templates: Array<any>;
  }>(GET_DB_SUBSCRIPTION_TEMPLATES, {
    variables: {
      feature_id: featureId
    }
  });

  const [saveProjectTemplateMutation] = useCIQMutation(
    MUTATION_UPDATE_SUBSCRIPTION_TEMPLATE_API
  );

  const [setDefaultDBTemplate] = useCIQMutation(
    MUTATION_SET_DEFAULT_SUBSCRIPTION_WF_TEMPLATE
  );

  const clonedDBTemplatesSoruce = useMemo(() => {
    if (!DBTemplates?.subscription_workflow_templates) return null;
    return structuredClone(DBTemplates.subscription_workflow_templates);
  }, [DBTemplates]);

  useEffect(() => {
    if (!DBTemplates?.subscription_workflow_templates) return;
    setfeatureTemplates(clonedDBTemplatesSoruce);
  }, [DBTemplates, clonedDBTemplatesSoruce]);

  const [copyDBTemplateMutation] = useCIQMutation(
    MUTATION_COPY_SUBSCRIPTION_TEMPLATE,
    {}
  );

  const [disableDBTemplate] = useCIQMutation(
    MUTATION_UPDATE_SUBSCRIPTION_MDB_TEMPLATE
  );

  const canEditFeatureDBTemplate = useMemo(
    () => subscriptionRole > RoleSubscriptionEnum.subscription_viewer,
    [subscriptionRole]
  );

  const templateObjectForDetailsView = useMemo(() => {
    if (!detailsViewData || !DBTemplates?.subscription_workflow_templates)
      return null;
    return DBTemplates.subscription_workflow_templates.find((template: any) => {
      return template.id === detailsViewData.data.id;
    });
  }, [DBTemplates, detailsViewData]);

  const onSaveTemplate = async (data: TSaveWorkflowModelInput) => {
    const res = await saveProjectTemplateMutation({
      variables: data
    });
    if (res.data) {
      message.success("Template updated successfully.");
    }
    if (res.errors && res.errors.length > 0) {
      message.error(res.errors[0].message);
    }
  };

  const onChangeDefaultTemplate = useCallback(
    async (cellData: any) => {
      if (!DBTemplates?.subscription_workflow_templates) return;
      setSavingDefaultTemplate(true);

      const updatedTemplates = DBTemplates.subscription_workflow_templates.map(
        (template: any) => {
          if (cellData.id === template.id) {
            return {
              ...template,
              default: true
            };
          }
          return {
            ...template,
            default: false
          };
        }
      );

      setfeatureTemplates([...updatedTemplates]);

      const payload: any = {
        variables: {
          templateId: cellData.id,
          feature_id: featureId
        }
      };

      const setDefaultTemplateResponse = await setDefaultDBTemplate(payload);

      if (setDefaultTemplateResponse.success) {
        message.success(SuccessMessages.DBDefaultTemplateSet);
      } else {
        setfeatureTemplates(clonedDBTemplatesSoruce);
      }
      setSavingDefaultTemplate(false);
    },
    [DBTemplates, clonedDBTemplatesSoruce, featureId, setDefaultDBTemplate]
  );

  const onWorkflowCopy = async (payload: any) => {
    const copyTemplateResponse = await copyDBTemplateMutation(payload);
    if (copyTemplateResponse.success) {
      message.success(SuccessMessages.DBTemplateCopiedSuccessfully);
    }
  };

  const onToggleTemplateDisabled = async (data: any) => {
    setSavingDefaultTemplate(true);

    const clonedTemplatesToRevert = structuredClone(featureTemplates);
    const updatedToggleMaterialList = featureTemplates.map((template: any) => {
      if (template.id === data.id) {
        return {
          ...template,
          disabled: !data.disabled
        };
      }
      return template;
    });

    setfeatureTemplates(updatedToggleMaterialList);
    const toggleDisableResp = await disableDBTemplate({
      variables: {
        pk_columns: { id: data.id },
        _set: { disabled: !data.disabled }
      }
    });

    if (!toggleDisableResp.success) {
      setfeatureTemplates(clonedTemplatesToRevert);
      message.error(ErrorMessages.disableTemplateFailed);
      return;
    }
    setSavingDefaultTemplate(false);
    message.success(
      data.disabled
        ? SuccessMessages.DBTemplateEnabledSuccessfully
        : SuccessMessages.DBTemplateDisabledSuccessfully
    );
  };

  return (
    <div className="py-[2px] px-4 pl-[50px] pr-[50px]">
      <div className="flex-col pt-[30px] mb-[40px]">
        <div className="flex w-full h-[40px] pt-[6px] px-6">
          <div className="flex text-ciq-gray-950 font-bold text-xl pr-[10px]">
            {FeatureIdTemplateMapping[featureId]} Templates
          </div>
          <div className="flex h-fit flex-wrap items-center border border-solid border-ciq-gray-300 rounded-xl py-1 px-2 text-black text-opacity-50 text-xs font-normal">
            {featureTemplates?.length}
          </div>
        </div>
        <div className="flex items-center w-full info-label pl-[25px]">
          <div className="flex text-justify">
            Create types of workflows to apply to{" "}
            {FeatureIdTemplateMapping[featureId]}s
          </div>
        </div>
      </div>
      {!detailsViewData && (
        <FeatureTemplateList
          setdetailsViewData={setdetailsViewData}
          workflowTemplates={featureTemplates}
          canEditFeatureDBTemplate={canEditFeatureDBTemplate}
          onChangeDefaultTemplate={onChangeDefaultTemplate}
          savingDefaultTemplate={savingDefaultTemplate}
          onWorkflowCopy={onWorkflowCopy}
          onToggleTemplateDisabled={onToggleTemplateDisabled}
          featureId={featureId}
        />
      )}
      {templateObjectForDetailsView && (
        <div className="py-[2px] px-4 pl-[25px] pr-[50px]">
          <div className="flex items-center space-x-3 py-1 mt-0.5">
            <Space
              className="flex items-center cursor-pointer"
              onClick={() => {
                setdetailsViewData(null);
              }}
            >
              <ArrowLeftOutlined />
              <span className="text-base font-medium uppercase whitespace-pre">
                {templateObjectForDetailsView.name}
              </span>
            </Space>
            {templateObjectForDetailsView.default && <DefaultTag />}
          </div>
          <div>
            {
              instructionsTextForFeatures[
                featureId as keyof typeof instructionsTextForFeatures
              ]
            }
          </div>
          <div className="py-3">
            <WorkflowTemplateEditor
              milestones={
                templateObjectForDetailsView.subscription_template_milestones
              }
              workflowTemplate={templateObjectForDetailsView}
              otherTemplateNames={DBTemplates?.subscription_workflow_templates
                .filter((x: any) => x.id !== templateObjectForDetailsView.id)
                .map((x: any) => x?.name?.toLowerCase())}
              disable={
                !canEditFeatureDBTemplate ||
                templateObjectForDetailsView.disabled
              }
              openInEditMode={detailsViewData.openInEditMode}
              onSave={onSaveTemplate}
              isTemplateUsed={false}
              featureId={featureId}
            />
          </div>
        </div>
      )}
    </div>
  );
}
export default FeatureTemplateSubscriptionSettings;
